/** @jsxImportSource @emotion/react */
import { SerializedStyles } from '@emotion/react';
import { openChat } from '@shared/chat-js';
import { cn, useWindowSize } from '@shared/react-ui-core';

import Button from '../../UI/Button';

export interface ICallChatProps {
  title?: string;
  text?: string;
  darkBackground?: boolean;
  rounded?: boolean;
  callBtnText?: string;
  chatBtnText?: string;
  roundButtons?: boolean;
  css?: SerializedStyles;
  callBtnStyles?: SerializedStyles;
  chatBtnStyles?: SerializedStyles;
  callBtnProps?: {};
  chatBtnProps?: {};
  showChat?: boolean;
  className?: string;
  headingComponent?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

const CallChat: React.FC<ICallChatProps> = ({
  title = 'Want to learn more?',
  text = 'Chat with one of our friendly customer support agents. They’re available 24/7 to answer your questions.',
  css: propsCss,
  callBtnText = 'Call 855.268.3669',
  chatBtnText = 'Chat With Us',
  darkBackground = false,
  rounded = true,
  roundButtons = true,
  callBtnStyles = {},
  chatBtnStyles = {},
  callBtnProps = {},
  chatBtnProps = {},
  showChat = true,
  className,
  headingComponent = 'h3',
  ...props
}) => {
  const { width = 0 } = useWindowSize();
  const Component = headingComponent;

  return (
    <section
      className={cn(
        `text-cove-navy-blue px-cove-25 sm:px-cove-50 py-cove-50 mx-auto mt-0 w-full max-w-lg ${darkBackground && 'sm:bg-cove-navy-blue py-cove-75 [&>*]:text-cove-blue sm:[&>*]:text-cove-white bg-transparent px-0 shadow-none sm:shadow'} ${rounded && 'rounded-[15px]'}`,
        className
      )}
      {...props}
    >
      <Component
        className={`mb-cove-15 xs:text-center mx-auto mt-0 max-w-[720px] text-left text-lg font-normal leading-[30px] sm:text-[32px] sm:font-bold sm:leading-[39px] ${darkBackground ? 'text-cove-blue sm:text-cove-white' : 'text-cove-navy-blue'}`}
      >
        {title}
      </Component>
      {text.length ? (
        <p
          className={`mb-cove-35 mx-auto mt-0 max-w-[720px] text-left text-base font-normal sm:text-center ${darkBackground && 'text-cove-white'}`}
        >
          {text}
        </p>
      ) : null}
      <div className="gap-cove-25 sm:gap-cove-25 [&_button]:border-cove-blue mx-auto my-0 flex max-w-[540px] flex-col items-center justify-center sm:flex-row sm:items-start">
        <Button
          cypressId="call-us"
          href="tel:8552683669"
          color={'purple'}
          size="large"
          style={width <= 600 ? 'outlined' : 'text'}
          className={cn(
            `min-w-0 max-w-[270px] text-base sm:max-w-[185px]`,
            roundButtons && 'rounded-[25px]',
            darkBackground &&
              'first-of-type:text-cove-blue first-of-type:border-cove-blue sm:first-of-type:text-cove-white last-of-type:[&:not(disabled)]:hover:bg-cove-blue last-of-type:[&:not(disabled)]:hover:text-cove-white first-of-type:border-2 sm:first-of-type:border-none',
            callBtnStyles
          )}
          {...callBtnProps}
        >
          {callBtnText}
        </Button>
        {showChat && (
          <Button
            id="chat"
            className={cn(
              'w-[270px]',
              roundButtons && 'rounded-[25px]',
              darkBackground &&
                'first-of-type:text-cove-blue first-of-type:border-cove-blue sm:first-of-type:text-cove-white last-of-type:[&:not(disabled)]:hover:bg-cove-blue last-of-type:[&:not(disabled)]:hover:text-cove-white first-of-type:border-2 sm:first-of-type:border-none',
              chatBtnStyles
            )}
            onClick={() => openChat()}
            chatButton
            {...{
              style: 'outlined',
              color: 'purple',
              size: 'large',
            }}
            {...chatBtnProps}
          >
            {chatBtnText}
          </Button>
        )}
      </div>
    </section>
  );
};

export default CallChat;
